import { httpService } from '../services/httpService'
import { convertPrice } from './price'

import { IUserACH, IUserCard, IUserInterface } from '../models/users/user.interface'
import { IRegistrationFeesInterface } from '../models/registration-fees/registrationFees.interface'
import { FEES_CATEGORY_CONST } from '../components/events/views/details/EventDetailsViewComponentFees'
import { IPaymentStatus, IPaymentType, PAYMENT_STATUS, PAYMENT_TYPE } from '../types/payment'
import { IRegistrationByDayInterface } from '../models/registrations-by-day/registrationByDay.interface'
import { IRegistrationTicketInterface } from '../models/registration-tickets/registrationTicket.interface'
import {
  ICurrency,
  IPaymentOption,
} from '../models/event-payment-settings/event-payment-settings.interface'
import { currencies } from '../fakeData/currencies'

type ICardPaymentMethod = {
  id: string
  object: 'payment_method'
  allow_redisplay: string
  billing_details: {
    address: {
      city: string | null
      country: string | null
      line1: string | null
      line2: string | null
      postal_code: string
      state: string | null
    }
    email: string | null
    name: string | null
    phone: string | null
    tax_id: string | null
  }
  card: {
    brand: string
    checks: {
      address_line1_check: string | null
      address_postal_code_check: string
      cvc_check: string
    }
    country: string
    display_brand: string
    exp_month: number
    exp_year: number
    fingerprint: string
    funding: string
    generated_from: null
    last4: string
    networks: {
      available: string[]
      preferred: string | null
    }
    regulated_status: string
    three_d_secure_usage: {
      supported: boolean
    }
    wallet: string | null
  }
  created: number
  customer: string
  livemode: boolean
  metadata: {}
  type: 'card'
}

type IACHPaymentMethod = {
  id: string
  object: string
  allow_redisplay: string
  billing_details: {
    address: {
      city: string | null
      country: string | null
      line1: string | null
      line2: string | null
      postal_code: string | null
      state: string | null
    }
    email: string | null
    name: string
    phone: string | null
    tax_id: string | null
  }
  created: number
  customer: string
  livemode: boolean
  metadata: {}
  type: 'us_bank_account'
  us_bank_account: {
    account_holder_type: 'individual' | 'company'
    account_type: 'checking' | 'savings'
    bank_name: string
    financial_connections_account: string | null
    fingerprint: string
    last4: string
    networks: {
      preferred: string
      supported: string[]
    }
    routing_number: string
    status_details: {}
  }
}

export const getPaymentMethodsFormatted = async (
  userEmail: string,
  userStripeAccountId?: string | null
) => {
  try {
    if (userStripeAccountId) {
      const res: {
        cardPaymentMethods: ICardPaymentMethod[]
        ACHPaymentMethods: IACHPaymentMethod[]
      } = await httpService({
        url: `get_payment_methods/${userEmail}/${userStripeAccountId}`,
      })

      const cardPaymentMethods: IUserCard[] = []
      res?.cardPaymentMethods?.forEach((payment) => {
        cardPaymentMethods.push({
          cardNumber: payment.card.last4,
          cardType: payment.card.brand,
          pmId: payment.id,
          customerId: payment.customer,
          expiryMonth: payment.card.exp_month,
          expiryYear: payment.card.exp_year,
          type: payment.type,
          default: false,
        })
      })

      const ACHPaymentMethods: IUserACH[] = []
      res?.ACHPaymentMethods?.forEach((payment) => {
        ACHPaymentMethods.push({
          cardNumber: payment.us_bank_account.last4,
          bankName: payment.us_bank_account.bank_name,
          pmId: payment.id,
          customerId: payment.customer,
          routingNumber: payment.us_bank_account.routing_number,
          type: payment.type,
          default: false,
        })
      })

      return { CARDS: cardPaymentMethods, ACH: ACHPaymentMethods }
    }
    return { CARDS: [], ACH: [] }
  } catch (error) {
    console.error(error, 'error')
    return { CARDS: [], ACH: [] }
  }
}

export type ITotalPrices = {
  paid: number
  paidSalesTax: number
  unpaid: number
  unpaidSalesTax: number
  refund: number
  scratch: number
  scratchSalesTax: number
  paypal: number
  paypalSalesTax: number
  check: number
  checkSalesTax: number
  cash: number
  cashSalesTax: number
  card: number
  cardSalesTax: number
  venmo: number
  venmoSalesTax: number
}

type IPriceItem = {
  paymentType: IPaymentType
  paymentStatus: IPaymentStatus
  isScratched: boolean | undefined
  refundAmountAdded: boolean | undefined
  amountRefunded: number
  includeSalesTax: boolean
  price: number
  created: string | Date | null | undefined
}

const getItemPrice = (item: IPriceItem, isChargeSalesTax: boolean) => {
  const total = convertPrice(item?.price)
  const salesTax = addSalesTax(item.created, total, item.includeSalesTax, isChargeSalesTax)
  return { total, salesTax }
}

const getTotals = (items: IPriceItem[], isChargeSalesTax: boolean) => {
  const totals = {
    paid: 0,
    paidSalesTax: 0,
    unpaid: 0,
    unpaidSalesTax: 0,
    refund: 0,
    scratch: 0,
    scratchSalesTax: 0,
    paypal: 0,
    paypalSalesTax: 0,
    check: 0,
    checkSalesTax: 0,
    cash: 0,
    cashSalesTax: 0,
    card: 0,
    cardSalesTax: 0,
    venmo: 0,
    venmoSalesTax: 0,
  }

  items.forEach((item) => {
    if (item.paymentStatus === PAYMENT_STATUS.PAID) {
      if (item.paymentType !== PAYMENT_TYPE.DEFAULT) {
        totals.paid += getItemPrice(item, isChargeSalesTax).total
        totals.paidSalesTax += getItemPrice(item, isChargeSalesTax).salesTax
      }
      switch (item.paymentType) {
        case PAYMENT_TYPE.PAYPAL:
          totals.paypal += getItemPrice(item, isChargeSalesTax).total
          totals.paypalSalesTax += getItemPrice(item, isChargeSalesTax).salesTax
          break
        case PAYMENT_TYPE.CHECK:
          totals.check += getItemPrice(item, isChargeSalesTax).total
          totals.checkSalesTax += getItemPrice(item, isChargeSalesTax).salesTax
          break
        case PAYMENT_TYPE.CASH:
          totals.cash += getItemPrice(item, isChargeSalesTax).total
          totals.cashSalesTax += getItemPrice(item, isChargeSalesTax).salesTax
          break
        case PAYMENT_TYPE.CARD:
          totals.card += getItemPrice(item, isChargeSalesTax).total
          totals.cardSalesTax += getItemPrice(item, isChargeSalesTax).salesTax
          break
        case PAYMENT_TYPE.VENMO:
          totals.venmo += getItemPrice(item, isChargeSalesTax).total
          totals.venmoSalesTax += getItemPrice(item, isChargeSalesTax).salesTax
          break
      }
    }
    if (
      item.paymentStatus === PAYMENT_STATUS.DEFAULT &&
      item.paymentType !== PAYMENT_TYPE.DEFAULT &&
      !item.isScratched
    ) {
      totals.unpaid += getItemPrice(item, isChargeSalesTax).total
      totals.unpaidSalesTax += getItemPrice(item, isChargeSalesTax).salesTax
    }

    if (item.refundAmountAdded && item.paymentType !== PAYMENT_TYPE.DEFAULT) {
      totals.refund += convertPrice(item.amountRefunded)
    }

    if (item.isScratched && item.paymentType !== PAYMENT_TYPE.DEFAULT) {
      totals.scratch += getItemPrice(item, isChargeSalesTax).total
      totals.scratchSalesTax += getItemPrice(item, isChargeSalesTax).salesTax
    }
  })

  return totals
}

type IGetUnpaidTotal = {
  registrationsEntries?: IRegistrationByDayInterface[]
  registrationsFees?: IRegistrationFeesInterface[]
  registrationsTickets?: IRegistrationTicketInterface[]
  isChargeSalesTax: boolean
}

export const getTotalPrices = ({
  registrationsEntries,
  registrationsFees,
  registrationsTickets,
  isChargeSalesTax,
}: IGetUnpaidTotal) => {
  let totalRegistrations = getTotals(
    registrationsEntries?.map((item) => ({
      paymentType: item.paymentType,
      paymentStatus: item.paymentStatus,
      isScratched: item.isScratched,
      refundAmountAdded: item.refundAmountAdded,
      amountRefunded: item.amountRefunded,
      includeSalesTax: item.includeSalesTax,
      created: item.created,
      price: (item.registrationPrice || 0) + (item.isQualify ? Number(item.qualifyFee) || 0 : 0),
    })) || [],
    isChargeSalesTax
  )

  let totalFees = getTotals(
    registrationsFees
      ?.filter((fee) => fee.feesItemCategory !== FEES_CATEGORY_CONST.GOVERNANCE)
      .map((item) => ({
        paymentType: item.paymentType,
        paymentStatus: item.paymentStatus,
        isScratched: item.isScratched,
        refundAmountAdded: item.refundAmountAdded,
        amountRefunded: item.amountRefunded,
        includeSalesTax: item.includeSalesTax,
        created: item.created,
        price: (item.feesPrice || 0) * (item.selectedUnitsCount || 1),
      })) || [],
    isChargeSalesTax
  )

  let totalGovernance = getTotals(
    registrationsFees
      ?.filter((fee) => fee.feesItemCategory === FEES_CATEGORY_CONST.GOVERNANCE)
      .map((item) => ({
        paymentType: item.paymentType,
        paymentStatus: item.paymentStatus,
        isScratched: item.isScratched,
        refundAmountAdded: item.refundAmountAdded,
        amountRefunded: item.amountRefunded,
        includeSalesTax: item.includeSalesTax,
        created: item.created,
        price: (item.feesPrice || 0) * (item.selectedUnitsCount || 1),
      })) || [],
    isChargeSalesTax
  )

  let totalTickets = getTotals(
    registrationsTickets?.map((item) => ({
      paymentType: item.paymentType,
      paymentStatus: item.paymentStatus,
      isScratched: item.isScratched,
      refundAmountAdded: item.refundAmountAdded,
      amountRefunded: item.amountRefunded,
      includeSalesTax: item.includeSalesTax,
      created: item.created,
      price: (item.ticketPrice || 0) * (item.selectedUnitsCount || 1),
    })) || [],
    isChargeSalesTax
  )

  const unpaidTotal =
    totalRegistrations.unpaid +
    totalRegistrations.unpaidSalesTax +
    totalFees.unpaid +
    totalFees.unpaidSalesTax +
    totalGovernance.unpaid +
    totalGovernance.unpaidSalesTax +
    totalTickets.unpaid +
    totalTickets.unpaidSalesTax

  const refundTotal =
    totalRegistrations.refund + totalFees.refund + totalGovernance.refund + totalTickets.refund

  const scratchTotal =
    totalRegistrations.scratch + totalFees.scratch + totalGovernance.scratch + totalTickets.scratch

  const scratchTax =
    totalRegistrations.scratchSalesTax +
    totalFees.scratchSalesTax +
    totalGovernance.scratchSalesTax +
    totalTickets.scratchSalesTax

  const paidTotal =
    totalRegistrations.paid +
    totalRegistrations.paidSalesTax +
    totalFees.paid +
    totalFees.paidSalesTax +
    totalGovernance.paid +
    totalGovernance.paidSalesTax +
    totalTickets.paid +
    totalTickets.paidSalesTax

  const paidByPaypal =
    totalRegistrations.paypal +
    totalRegistrations.paypalSalesTax +
    totalFees.paypal +
    totalFees.paypalSalesTax +
    totalGovernance.paypal +
    totalGovernance.paypalSalesTax +
    totalTickets.paypal +
    totalTickets.paypalSalesTax

  const paidByCash =
    totalRegistrations.cash +
    totalRegistrations.cashSalesTax +
    totalFees.cash +
    totalFees.cashSalesTax +
    totalGovernance.cash +
    totalGovernance.cashSalesTax +
    totalTickets.cash +
    totalTickets.cashSalesTax

  const paidByCheck =
    totalRegistrations.check +
    totalRegistrations.checkSalesTax +
    totalFees.check +
    totalFees.checkSalesTax +
    totalGovernance.check +
    totalGovernance.checkSalesTax +
    totalTickets.check +
    totalTickets.checkSalesTax

  const paidByCard =
    totalRegistrations.card +
    totalRegistrations.cardSalesTax +
    totalFees.card +
    totalFees.cardSalesTax +
    totalGovernance.card +
    totalGovernance.cardSalesTax +
    totalTickets.card +
    totalTickets.cardSalesTax

  const paidByVenmo =
    totalRegistrations.venmoSalesTax +
    totalRegistrations.venmo +
    totalFees.venmo +
    totalFees.venmoSalesTax +
    totalGovernance.venmo +
    totalGovernance.venmoSalesTax +
    totalTickets.venmo +
    totalTickets.venmoSalesTax

  const totalUnpaidTax =
    totalRegistrations.unpaidSalesTax +
    totalFees.unpaidSalesTax +
    totalGovernance.unpaidSalesTax +
    totalTickets.unpaidSalesTax

  const totalPaidTax =
    totalRegistrations.paidSalesTax +
    totalFees.paidSalesTax +
    totalGovernance.paidSalesTax +
    totalTickets.paidSalesTax

  const totalTax = totalUnpaidTax + totalPaidTax

  return {
    totalRegistrations,
    totalFees,
    totalGovernance,
    totalTickets,
    totalTax,
    totalPaidTax,
    unpaidTotal,
    paidTotal,
    refundTotal,
    scratchTotal,
    scratchTax,
    paidByCash,
    paidByPaypal,
    paidByCheck,
    paidByCard,
    paidByVenmo,
  }
}

// add sales tax to the amount
export const addSalesTax = (
  created: string | Date | null | undefined,
  amount: number,
  includeSalesTax: boolean,
  isChargeSalesTax: boolean | undefined
) => {
  // TODO: We'll need to remove “03-24-2026” later, but for now it's necessary to support older events that don't have the “includeSalesTax” key
  const haveSalesTax =
    created && new Date(created).getTime() > new Date('03-24-2026').getTime()
      ? includeSalesTax || false
      : isChargeSalesTax || false

  if (!haveSalesTax) return 0
  return amount * 0.02 || 0
}

export const isRefundPending = (
  isScratched: boolean = false,
  isRefunded: boolean = false,
  paymentStatus: IPaymentStatus,
  paymentType: IPaymentType
): boolean => {
  return (
    isScratched &&
    !isRefunded &&
    paymentStatus === PAYMENT_STATUS.PAID &&
    paymentType === PAYMENT_TYPE.CARD
  )
}

export const isPaymentPending = (
  isScratched: boolean = false,
  paymentStatus: IPaymentStatus
): boolean => {
  return !isScratched && paymentStatus === PAYMENT_STATUS.DEFAULT
}

export const getIncrement = (paymentOption: IPaymentOption) => {
  let incrementBy = 0

  switch (paymentOption) {
    case 'IncreaseAllPrices':
      incrementBy = 0.05
      break

    case 'ChargeRidersAndSpectators':
      incrementBy = 0.025
      break
  }

  return incrementBy
}

export const getPaymentRequestStatus = (
  pmId: string,
  requesterId: string,
  owner: IUserInterface
) => {
  const paymentMethod = owner?.paymentMethodsAccess?.[pmId]?.find(
    (item) => item.requesterId === requesterId
  )
  return paymentMethod?.status || null
}

const buildCurrencySymbolIndex = () => {
  const map = new Map<string, ICurrency>()

  for (const [code, data] of Object.entries(currencies)) {
    map.set(data.symbol, code as ICurrency)

    if (data.symbol_native && data.symbol_native !== data.symbol) {
      map.set(data.symbol, code as ICurrency)
    }
  }

  return Array.from(map.entries()).sort(([a], [b]) => b.length - a.length)
}

const currencySymbolIndex = buildCurrencySymbolIndex()

export const getCurrencyFromAmount = (value: string | number, fallback: string = '$'): string => {
  const v = String(value).trim()

  for (const [symbol] of currencySymbolIndex) {
    if (v.startsWith(symbol) || v.endsWith(symbol)) {
      return symbol
    }
  }

  return fallback
}

export const getCurrency = (currency: ICurrency | undefined) => {
  return currencies[currency || 'USD']?.symbol || '$'
}
